import React, { useState } from 'react';
import { useAdminData } from '../../../contexts/AdminDataContext';
import type { CaseEntry } from '../../../types/caseEntry';
import type { Case } from '../../../types/admin';
import { caseEntryTotals, sortEntries } from '../../../utils/caseEntries';
import { estimateTotals, formatHoursShort, groupOfLine, groupTypes } from '../../../utils/caseEstimate';
import type { ConsumptionGroup } from '../../../utils/caseEstimate';
import { formatKr } from '../../../utils/quote';
import { kindOfEntry } from './useCaseEntryForm';
import type { EntryKind } from './useCaseEntryForm';
import { ConsumptionOverview } from './ConsumptionOverview';
import { ConsumptionSection } from './ConsumptionSection';
import { HoursSection } from './HoursSection';
import { CostSection } from './CostSection';
import { EntryDialog } from './EntryDialog';
import type { EntryDialogTarget } from './EntryDialog';
const costSections: {
  group: Exclude<ConsumptionGroup, 'Timer'>;
  kind: EntryKind;
  addLabel: string;
  emptyText: string;
}[] = [{
  group: 'Materialer',
  kind: 'material',
  addLabel: 'Tilføj materiale',
  emptyText: 'Ingen materialer på sagen endnu.'
}, {
  group: 'Kørsel & service',
  kind: 'travel',
  addLabel: 'Tilføj kørsel/service',
  emptyText: 'Ingen kørsel eller service endnu.'
}, {
  group: 'Øvrigt',
  kind: 'expense',
  addLabel: 'Tilføj udgift',
  emptyText: 'Ingen maskiner, bortskaffelse eller andre udgifter endnu.'
}];

/**
 * Forbrug på sagen som en arbejdslog: et kort overblik og fire foldbare
 * sektioner. Tilbuddet er estimatet (læses kun); sagen er det faktiske forbrug.
 */
export function CaseEntries({
  caseItem
}: {
  caseItem: Case;
}) {
  const {
    updateCase,
    quotes
  } = useAdminData();
  const entries = caseItem.entries ?? [];
  const actual = caseEntryTotals(entries);

  // Estimatet hentes fra det tilknyttede tilbud — ellers fra kopien på sagen.
  const quote = caseItem.quoteId ? quotes.find(entry => entry.id === caseItem.quoteId) : undefined;
  const quoteLines = quote?.lines?.length ? quote.lines : caseItem.quoteLines ?? [];
  const hasEstimate = Boolean(caseItem.quoteId) && quoteLines.length > 0;
  const estimate = hasEstimate ? estimateTotals(quoteLines) : null;
  const linesFor = (group: ConsumptionGroup) => hasEstimate ? quoteLines.filter(line => groupOfLine(line) === group) : [];
  const [open, setOpen] = useState<Record<ConsumptionGroup, boolean>>({
    Timer: false,
    Materialer: false,
    'Kørsel & service': false,
    Øvrigt: false
  });
  const [dialog, setDialog] = useState<EntryDialogTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const toggle = (group: ConsumptionGroup) => setOpen(current => ({
    ...current,
    [group]: !current[group]
  }));
  async function persist(next: CaseEntry[]): Promise<boolean> {
    setSaving(true);
    setSaveError('');
    const result = await updateCase(caseItem.id, {
      entries: next
    });
    setSaving(false);
    if (!result.ok) {
      setSaveError(result.error ?? 'Forbruget kunne ikke gemmes. Prøv igen.');
      return false;
    }
    return true;
  }
  function handleEdit(entry: CaseEntry) {
    setDialog({
      caseId: caseItem.id,
      kind: kindOfEntry(entry),
      entry
    });
  }
  async function handleDelete(entry: CaseEntry) {
    if (saving) return;
    if (!window.confirm(`Slet "${entry.description}"?`)) return;
    await persist(entries.filter(item => item.id !== entry.id));
  }
  function add(kind: EntryKind, group: ConsumptionGroup) {
    setOpen(current => ({
      ...current,
      [group]: true
    }));
    setDialog({
      caseId: caseItem.id,
      kind
    });
  }
  const registeredFor = (group: ConsumptionGroup) => entries.filter(entry => groupTypes[group].includes(entry.type));
  const moneySummary = (group: Exclude<ConsumptionGroup, 'Timer'>, registered: number) => {
    const est = estimate ? {
      Materialer: estimate.materials,
      'Kørsel & service': estimate.travelService,
      Øvrigt: estimate.other
    }[group] : 0;
    return estimate && est > 0 ? `${formatKr(registered)} af ${formatKr(est)}` : formatKr(registered);
  };
  const registeredMoney: Record<Exclude<ConsumptionGroup, 'Timer'>, number> = {
    Materialer: actual.materials,
    'Kørsel & service': actual.travelService,
    Øvrigt: actual.other
  };
  return <section className="rounded-card border border-line bg-white p-5 md:p-6" aria-labelledby="forbrug-titel" data-id="element-98" data-mp="2qbw2bp5vnhbd">
      <h2 id="forbrug-titel" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-99" data-mp="3iygw0w9ts5i3">
        Forbrug på sagen
      </h2>
      {!estimate && <p className="mt-1 text-sm text-ink-muted" data-id="element-100" data-mp="3nvyyc3tc7mh4">Ingen estimerede værdier fra tilbud</p>}

      <div className="mt-4" data-id="element-101" data-mp="397ceppwog8k3">
        <ConsumptionOverview estimate={estimate} actual={actual} data-id="element-102" data-mp="396b5lqz81w50" />
      </div>

      {saveError && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-103" data-mp="3e5dy6by0m9w3">
          {saveError}
        </p>}

      <div className="mt-5 rounded-xl border border-line px-4" data-id="element-104" data-mp="5pkmcaempmld">
        <ConsumptionSection id="forbrug-timer" title="Timer" summary={estimate && estimate.hoursCount > 0 ? `${formatHoursShort(actual.hoursCount)} af ${formatHoursShort(estimate.hoursCount)}` : formatHoursShort(actual.hoursCount)} addLabel="Registrér timer" open={open.Timer} onToggle={() => toggle('Timer')} onAdd={() => add('hours', 'Timer')} data-id="element-105" data-mp="aj5j5z4hkaiz">
          <HoursSection estimatedHours={estimate ? estimate.hoursCount : null} entries={sortEntries(registeredFor('Timer'))} onEdit={handleEdit} onDelete={handleDelete} data-id="element-106" data-mp="1c9562gpu10vj" />
        </ConsumptionSection>

        {costSections.map(section => <ConsumptionSection key={section.group} id={`forbrug-${section.kind}`} title={section.group} summary={moneySummary(section.group, registeredMoney[section.group])} addLabel={section.addLabel} open={open[section.group]} onToggle={() => toggle(section.group)} onAdd={() => add(section.kind, section.group)} data-id="element-107" data-mp="1h8rr3cbwjlew">
            <CostSection title={section.group} types={groupTypes[section.group]} showLabel={section.group !== 'Materialer'} emptyText={section.emptyText} estimateLines={linesFor(section.group)} allEntries={entries} saving={saving} onPersist={persist} onEdit={handleEdit} onDelete={handleDelete} data-id="element-108" data-mp="1uaspvdj9ei4m" />
          </ConsumptionSection>)}
      </div>

      <EntryDialog target={dialog} onClose={() => setDialog(null)} data-id="element-109" data-mp="1flwm3so8jr9t" />
    </section>;
}